/** Reusable small modules: pickups, telegraphed hazards, ballistic bodies
 * (projectiles, debris, falling hazards), the gun + projectile pool, debris
 * bursts and the static body. */
import { bool, id, int, num, obj, oneOf, readsSignal } from '../schemaBuilders.js';
import { defineModule, num as read, type ModuleCtx } from '../module.js';
import { confineX, landingSurface, pushVelocity, surfaces } from '../physics.js';
import { addTag, overlaps, removeTag, type EntityV1 } from '../world.js';
import { takeLayoutSize } from './surfaces.js';

/** No motion at all (pipes, scenery, ladders, targets). A placed instance
 * takes its layout rectangle's size (a ladder spans its tiers). */
export const staticBody = defineModule<Record<string, never>>({
  id: 'body.static', version: 1, attach: 'entity', role: 'movement',
  summary: 'An entity that never moves; a placed instance takes its layout rectangle as its size.',
  config: obj({}), requires: [], events: [], presentation: {}, proofs: ['never-moves', 'instance-size-from-layout'],
  animStates: () => [],
  init(_ctx, e, params) { takeLayoutSize(e, params); },
  systems: {},
});

export type PickupConfig = { collectorTag: string; score: number; collectOnHitFromBelow: boolean };

/** Solver pull of a pickup: richer ones pull harder. */
const pickupWeight = (c: PickupConfig) => 2 + c.score / 1000;

export const pickup = defineModule<PickupConfig>({
  id: 'rule.pickup', version: 1, attach: 'entity', role: 'rule',
  summary: 'Collected (scored to the collector’s seat, then removed) on touch, or when bumped from below.',
  config: obj({ collectorTag: id(), score: int(0, 100000), collectOnHitFromBelow: bool() }),
  requires: [], events: ['collect'], presentation: { collect: { cue: 'collect', vfx: { effect: 'burst', palette: 'slot', count: 6 } } }, proofs: ['touch-collects-and-scores-seat', 'non-collector-ignored', 'bumped-collects'],
  animStates: () => [],
  systems: {
    interact(ctx, e) {
      if (!e || e.status !== 'active') return;
      const c = ctx.config;
      let by = ctx.entities({ tag: c.collectorTag, status: 'active' }).find(p => overlaps(ctx.world, p, e));
      if (!by && c.collectOnHitFromBelow) {
        const bump = ctx.signals('hit-from-below').find(s => s.target === e.id);
        by = bump ? ctx.entity(bump.source) : undefined;
      }
      if (!by) return;
      if (by.slot !== null) ctx.addScore(by.slot, c.score);
      const counted = ctx.state[`collected:${e.archetype}`];
      ctx.state[`collected:${e.archetype}`] = (typeof counted === 'number' ? counted : 0) + 1;
      ctx.emit('collect', { x: e.x, y: e.y - e.h / 2, slot: by.slot ?? 0, points: c.score, kind: e.archetype });
      ctx.signal('collected', e, by, { x: e.x, y: e.y - e.h / 2, tags: [...e.tags] });
      ctx.despawn(e, 'collected');
    },
  },
  solverTargets(ctx, e) {
    return e && e.status === 'active' ? [{ x: e.x, y: e.y, weight: pickupWeight(ctx.config), label: 'collect' }] : [];
  },
  // This kind's pickups collected so far: reaching one is progress, not a lost pull.
  solverProgress(ctx, archetype) {
    const n = ctx.state[`collected:${archetype}`];
    return typeof n === 'number' ? n * (pickupWeight(ctx.config) + 1) : 0;
  },
});

export type TelegraphConfig = { warnTicks: number; activeTag: string; lifeTicks: number; warnAlpha: number };

/** Warn → act: the entity is drawn frozen and harmless (faded) for the
 * warning, then gains its active tag and is released; optional lifetime. */
export const telegraph = defineModule<TelegraphConfig>({
  id: 'rule.telegraph', version: 1, attach: 'entity', role: 'rule',
  summary: 'Telegraphed hazard: frozen and harmless (faded) for a warning window, then active with its hazard tag; optional lifetime.',
  config: obj({ warnTicks: int(1, 600), activeTag: id(), lifeTicks: int(0, 3600), warnAlpha: num(0.1, 1) }),
  requires: [], events: ['warn', 'strike'], presentation: { warn: { cue: 'countdown' }, strike: { cue: 'impact' } }, proofs: ['harmless-during-warning', 'activates-after-warning', 'expires-after-life'],
  includeHidden: true,
  animStates: () => [],
  init(ctx, e) {
    Object.assign(ctx.data(e), { timer: ctx.config.warnTicks, life: ctx.config.lifeTicks, warned: false });
    removeTag(e, ctx.config.activeTag);
    e.status = 'frozen'; e.alpha = ctx.config.warnAlpha;
  },
  systems: {
    think(ctx, e) {
      if (!e) return;
      const d = ctx.data(e), c = ctx.config;
      if (d.warned !== true) { d.warned = true; ctx.emit('warn', { x: e.x, y: e.y - e.h / 2 }); }
      if (read(d, 'timer') > 0) {
        d.timer = read(d, 'timer') - 1;
        if (read(d, 'timer') === 0) {
          e.status = 'active'; e.alpha = 1; addTag(e, c.activeTag);
          ctx.emit('strike', { x: e.x, y: e.y - e.h / 2 });
        }
        return;
      }
      if (c.lifeTicks > 0) {
        d.life = read(d, 'life') - 1;
        if (read(d, 'life') <= 0) ctx.despawn(e, 'expired');
      }
    },
  },
});

export type BallisticConfig = { gravity: number; lifeTicks: number; landOnSurfaces: boolean; leaveTag: string };

/** Projectiles, debris and falling hazards: integrate a spawn velocity
 * (`params.vx/vy`) under gravity, carried in flight by the push protocol (a
 * wind, a current); expire on lifetime or leaving the world; optionally stop
 * on landing (then shed `leaveTag`). */
export const ballistic = defineModule<BallisticConfig>({
  id: 'body.ballistic', version: 1, attach: 'entity', role: 'movement',
  summary: 'Ballistic body: spawn velocity under gravity, lifetime and out-of-world expiry, optional landing.',
  config: obj({ gravity: num(0, 120), lifeTicks: int(0, 3600), landOnSurfaces: bool(), leaveTag: id() }),
  requires: [], events: [], presentation: {}, proofs: ['straight-line-without-gravity', 'arc-with-gravity', 'push-protocol', 'expires', 'leaves-world', 'lands'],
  animStates: () => [],
  init(ctx, e, params) {
    e.vx = typeof params.vx === 'number' ? params.vx : 0;
    e.vy = typeof params.vy === 'number' ? params.vy : 0;
    ctx.data(e).age = 0;
  },
  systems: {
    move(ctx, e) {
      if (!e || e.status !== 'active') return;
      const c = ctx.config, d = ctx.data(e), world = ctx.world;
      d.age = read(d, 'age') + 1;
      if (c.lifeTicks > 0 && read(d, 'age') > c.lifeTicks) { ctx.despawn(e, 'expired'); return; }
      if (e.grounded) return;
      e.vy += c.gravity * ctx.dt;
      const prevFeet = e.y, push = pushVelocity(ctx.signals('push'), e), sy = e.vy + push.vy;
      e.x += (e.vx + push.vx) * ctx.dt; e.y += sy * ctx.dt;
      if (c.landOnSurfaces && sy > 0) {
        const land = landingSurface(world, surfaces(ctx), e.x, e.w / 2, prevFeet, e.y);
        if (land) { e.y = land.top; e.vx = 0; e.vy = 0; e.grounded = true; e.groundId = land.entity.id; removeTag(e, c.leaveTag); return; }
      }
      const out = e.y - e.h > world.bounds.h + 1 || e.y < -2 || (!world.bounds.wrapX && (e.x < -1 || e.x > world.bounds.w + 1));
      if (out) ctx.despawn(e, 'left-world');
      else if (world.bounds.wrapX) confineX(world, e);
    },
  },
});

export type GunConfig = { projectile: string; speed: number; cooldownTicks: number;
  /** `heading`: along `headingDeg` (0 = up, clockwise — a diagonal scroll's
   * forward); `target`: at the nearest active `aimTag` entity (holds fire
   * while there is none). */
  aim: 'facing' | 'up' | 'down' | 'heading' | 'target';
  headingDeg?: number;
  aimTag?: string;
  trigger: 'a' | 'b' | 'auto'; autoEveryTicks: number; maxAlive: number; muzzleY: number;
  /** Fires only while the entity carries this (dynamic) tag — a diver arms
   * itself on a dive, never in formation. Absent: always armed. */
  armTag?: string;
  /** Named scalar replacing `autoEveryTicks` when present (difficulty curves
   * tighten the cadence). */
  everyScalar?: string;
};

/** Fires a pooled projectile archetype (ballistic body) on a button or a
 * timer, with a cooldown and an alive cap; shows the `shoot` pose. An auto
 * gun's cadence counts from when it was last armed. */
export const gun = defineModule<GunConfig>({
  id: 'weapon.gun', version: 1, attach: 'entity', role: 'behaviour',
  summary: 'Gun: spawns a projectile archetype along facing/up/down on a button or timer; cooldown and live-projectile cap.',
  config: obj({ projectile: id(), speed: num(0.5, 60), cooldownTicks: int(1, 600), aim: oneOf('facing', 'up', 'down', 'heading', 'target'),
    headingDeg: num(-180, 180), aimTag: id(),
    trigger: oneOf('a', 'b', 'auto'), autoEveryTicks: int(1, 900), maxAlive: int(1, 16), muzzleY: num(0, 4), armTag: id(), everyScalar: id() },
  ['headingDeg', 'aimTag', 'armTag', 'everyScalar']),
  requires: ['body.ballistic'], events: ['fire'], presentation: { fire: { cue: 'shot' } },
  proofs: ['fires-on-press', 'cooldown', 'alive-cap', 'auto-cadence', 'aim', 'aim-heading', 'aim-at-target', 'armed-only-with-tag', 'scalar-cadence'],
  animStates: () => ['shoot'],
  mapArchetypeRefs: (c, rename) => ({ ...c, projectile: rename(c.projectile) }),
  check: (c, context) => [
    ...(context.archetypes[c.projectile]?.modules.includes('body.ballistic') ? [] : [`${c.projectile} must use body.ballistic`]),
    ...((c.aim === 'heading') !== (c.headingDeg !== undefined) ? ['exactly a heading aim sets headingDeg'] : []),
    ...((c.aim === 'target') !== (c.aimTag !== undefined) ? ['exactly a target aim sets aimTag'] : []),
  ],
  init(ctx, e) { Object.assign(ctx.data(e), { cooldown: 0, age: 0, shots: [], pose: 0 }); },
  systems: {
    think(ctx, e) {
      if (!e || e.status !== 'active') return;
      const c = ctx.config, d = ctx.data(e);
      if (read(d, 'cooldown') > 0) d.cooldown = read(d, 'cooldown') - 1;
      const shots = (d.shots as string[]).filter(sid => ctx.world.entities[sid] !== undefined);
      d.shots = shots;
      if (c.armTag !== undefined && !e.tags.includes(c.armTag)) { d.age = 0; return; }
      d.age = read(d, 'age') + 1;
      const every = Math.max(1, Math.round(c.everyScalar !== undefined ? ctx.scalar(c.everyScalar, c.autoEveryTicks) : c.autoEveryTicks));
      const input = e.slot !== null ? ctx.input(e.slot) : null;
      const pulled = c.trigger === 'auto' ? read(d, 'age') % every === 0
        : c.trigger === 'a' ? input?.a === true : input?.b === true;
      if (!pulled || read(d, 'cooldown') > 0 || shots.length >= c.maxAlive) return;
      const muzzle = { x: e.x, y: e.y - c.muzzleY };
      const v = aimVelocity(ctx, e, muzzle);
      if (!v) return;
      const shot = ctx.spawn(c.projectile, { x: muzzle.x, y: muzzle.y, facing: e.facing, params: { vx: v.vx, vy: v.vy } });
      shots.push(shot.id);
      d.cooldown = c.cooldownTicks;
      ctx.emit('fire', { x: e.x, y: e.y - c.muzzleY, slot: e.slot ?? 0 });
      d.pose = 6;
      d.fired = true;
    },
    present(ctx, e) {
      if (!e || e.status !== 'active') return;
      const d = ctx.data(e);
      if (read(d, 'pose') > 0) { ctx.setAnim(e, 'shoot', d.fired === true); d.pose = read(d, 'pose') - 1; }
      d.fired = false;
    },
  },
  // Line of fire: a seat's vertical gun pays off directly under (or over) a
  // destructible target, at the shooter's own height.
  solverTargets(ctx, e, player) {
    const aim = ctx.config.aim;
    if (!e || e.id !== player.id || aim === 'facing') return [];
    return ctx.entities({ status: 'active' })
      .filter(t => t.slot === null && ctx.hasModule(t, 'rule.destructible') && (aim === 'up' ? t.y < e.y - e.h : t.y > e.y))
      .map(t => ({ x: t.x, y: e.y, weight: 3, label: 'line-up', press: true }));
  },
});

/** A gun's launch velocity, or null when a target aim has nothing to aim at. */
function aimVelocity(ctx: ModuleCtx<GunConfig>, e: EntityV1, muzzle: { x: number; y: number }): { vx: number; vy: number } | null {
  const c = ctx.config;
  switch (c.aim) {
    case 'facing': return { vx: e.facing * c.speed, vy: 0 };
    case 'up': return { vx: 0, vy: -c.speed };
    case 'down': return { vx: 0, vy: c.speed };
    case 'heading': {
      const rad = (c.headingDeg! * Math.PI) / 180;
      return { vx: Math.sin(rad) * c.speed, vy: -Math.cos(rad) * c.speed };
    }
    case 'target': {
      let best: { dx: number; dy: number; d: number } | null = null;
      for (const t of ctx.entities({ tag: c.aimTag!, status: 'active' })) {
        const dx = t.x - muzzle.x, dy = t.y - t.h / 2 - muzzle.y, dist = Math.hypot(dx, dy);
        if (dist > 1e-6 && (!best || dist < best.d)) best = { dx, dy, d: dist };
      }
      return best ? { vx: (best.dx / best.d) * c.speed, vy: (best.dy / best.d) * c.speed } : null;
    }
  }
}

/** The seat credited for a projectile: its own seat, else the seat of the
 * weapon that fired it — the live-shot protocol: a weapon module lists its
 * live projectiles as `shots` in its component data (a gun's bullets, a bomb
 * bay's stores), and that list still names a shot spent this tick. Null when
 * no seat fired it. */
export function shooterSlot(ctx: ModuleCtx<unknown>, projectileId: string | null): number | null {
  if (projectileId === null) return null;
  const own = ctx.entity(projectileId);
  if (own && own.slot !== null) return own.slot;
  for (const e of ctx.entities({ status: 'any' })) {
    if (e.slot === null) continue;
    for (const key of Object.keys(e.c).sort()) {
      const shots = e.c[key]!.shots;
      if (Array.isArray(shots) && shots.includes(projectileId)) return e.slot;
    }
  }
  return null;
}

export type DebrisConfig = { on: string; fromTag: string; archetype: string; count: number; speed: number };

/** Spawns a burst of debris entities (ballistic) where a signal's target
 * was, e.g. on `defeated` or `despawned`. Debris is cosmetic but simulated
 * (deterministic, snapshot-safe). */
export const debris = defineModule<DebrisConfig>({
  id: 'fx.debris', version: 1, attach: 'global', role: 'presentation',
  summary: 'Debris burst: N ballistic shards fanned out where a signalled entity was.',
  config: obj({ on: readsSignal(oneOf('defeated', 'damage', 'collected')), fromTag: id(), archetype: id(), count: int(1, 12), speed: num(0.5, 30) }),
  requires: ['body.ballistic'], events: [], presentation: {}, proofs: ['burst-count', 'fan-out-symmetric', 'tag-filtered'],
  animStates: () => [],
  mapArchetypeRefs: (c, rename) => ({ ...c, archetype: rename(c.archetype) }),
  systems: {
    // Flow: after every rule that signals `defeated`/`damage`/`collected`.
    flow(ctx) {
      const c = ctx.config;
      // Signals carry where it happened (x, y, tags): the entity may be gone.
      for (const s of ctx.signals(c.on)) {
        const tags = s.data.tags;
        if (!Array.isArray(tags) || !tags.includes(c.fromTag) || typeof s.data.x !== 'number' || typeof s.data.y !== 'number') continue;
        for (let i = 0; i < c.count; i++) {
          const angle = -Math.PI * (i + 0.5) / c.count;
          const jitter = 0.75 + 0.5 * ctx.random();
          ctx.spawn(c.archetype, { x: s.data.x, y: s.data.y, params: { vx: Math.cos(angle) * c.speed * jitter, vy: Math.sin(angle) * c.speed * jitter } });
        }
      }
    },
  },
});

